import React from 'react';
import { FieldsProvider, useFields } from './State';
import { FieldSetContainer } from './FormFields';

// Unsaved Changes Banner
const UnsavedChangesBanner = () => {
  const { state } = useFields();

  const newCount = state.fields.filter(f => f.isNew && !f.destroy).length;
  const removedCount = state.fields.filter(f => f.destroy).length;

  if (newCount === 0 && removedCount === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
        <span className="text-sm font-medium text-amber-800">
          You have unsaved changes:
          {newCount > 0 && <span className="ml-1">{newCount} new field{newCount > 1 ? 's' : ''}</span>}
          {newCount > 0 && removedCount > 0 && <span>,</span>}
          {removedCount > 0 && <span className="ml-1">{removedCount} pending removal{removedCount > 1 ? 's' : ''}</span>}
        </span>
      </div>
      <span className="text-xs text-amber-600">
        Save your changes below
      </span>
    </div>
  );
};

const FieldsListContainer = () => {
  const { state } = useFields();

  if (state.fields.length === 0) {
    return (
      <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No custom fields</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by adding your first custom field.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {state.fields.map((field, index) => (
        <FieldSetContainer
          key={field.objectId}
          field={field}
          index={index}
          isFirst={index === 0}
        />
      ))}
    </div>
  );
};

const AddFieldButton = () => {
  const { addField } = useFields();

  const handleClick = (e) => {
    e.preventDefault();
    addField();
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
      </svg>
      Add Custom Field
    </button>
  );
};

export default (props) => {
  const { resource, resource_name, fields, dropdown_options, input_types } = props;

  return (
    <FieldsProvider
      resource={resource}
      resource_name={resource_name}
      fields={fields}
      dropdown_options={dropdown_options}
      input_types={input_types}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Custom Fields
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Add unique fields to collect additional information from your members
            </p>
          </div>
          <AddFieldButton />
        </div>

        {/* Unsaved Changes Banner */}
        <UnsavedChangesBanner />

        {/* Fields List */}
        <FieldsListContainer />

        {/* Helper Text */}
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          Remember to save your changes at the bottom of the page
        </p>
      </div>
    </FieldsProvider>
  );
};
